import {
  Button,
  DATE_FORMAT,
  DropdownItem,
  DropdownMenu,
} from '@dev-spendesk/grapes';
import React from 'react';

import { type LocaleFormat, useTranslation } from 'common/hooks/useTranslation';
import { useModal } from 'src/core/common/hooks/useModalGrapes';

import CustomPeriodModal from './CustomPeriodModal';
import { type PeriodRange } from '../../../../models/periodRange';

type Props = {
  range: PeriodRange;
  maxRange: { from: Date; to: Date };
  onChange: (range: PeriodRange) => void;
  align?: 'left' | 'right';
};

export const BudgetPeriodSelector = ({
  onChange,
  range,
  maxRange,
  align = 'right',
}: Props) => {
  const { t, localeFormat } = useTranslation('global');
  const options = [
    {
      key: 'overall',
      label: t('budget.breakdownSpendings.periods.overall'),
    },
    {
      key: 'custom',
      label: t('budget.breakdownSpendings.periods.customForm'),
    },
  ] satisfies { key: PeriodRange['key']; label: string }[];

  const [customPeriodModal, showCustomPeriodModal, hideCustomPeriodModal] =
    useModal(
      ({
        isOpen,
        selectedDateRange,
      }: {
        isOpen: boolean;
        maxRange: { from: Date; to: Date };
        onChange: ({ from, to }: { from: Date; to: Date }) => void;
        selectedDateRange: { from: Date; to: Date };
      }) => {
        if (!isOpen) {
          return <></>;
        }
        return (
          <CustomPeriodModal
            isOpen={isOpen}
            onClose={() => {
              hideCustomPeriodModal();
            }}
            maxRange={maxRange}
            onChange={(r) => {
              onChange({ key: 'custom', from: r.from, to: r.to });
              hideCustomPeriodModal();
            }}
            selectedDateRange={selectedDateRange}
          />
        );
      },
    );

  return (
    <>
      {customPeriodModal}
      <DropdownMenu
        options={options}
        placement={align === 'left' ? 'bottom-start' : 'bottom-end'}
        renderButton={(getToggleButtonProps) => (
          <Button
            {...getToggleButtonProps()}
            variant="secondaryNeutral"
            iconName={range.key === 'overall' ? 'eye' : undefined}
            iconPosition="left"
            text={rangeToLabel(range, options, localeFormat)}
          />
        )}
        renderOption={(option) => <DropdownItem label={option.label} />}
        onSelect={(option) => {
          if (option.key === 'custom') {
            showCustomPeriodModal();
          } else {
            onChange({ key: option.key });
          }
        }}
      />
    </>
  );
};

const rangeToLabel = (
  range: PeriodRange,
  options: { key: string; label: string }[],
  localeFormat: LocaleFormat,
): string => {
  if (range.key === 'custom') {
    return `${localeFormat(range.from, DATE_FORMAT.CUSTOM, {
      month: 'short',
      year: 'numeric',
    })} - ${localeFormat(range.to, DATE_FORMAT.CUSTOM, {
      month: 'short',
      year: 'numeric',
    })}`;
  }
  return options.find((option) => option.key === range.key)?.label ?? '';
};
